import React, { useState } from 'react';
import Dialog from './Dialog';
import useStore from '@store';
import { Lock, Eye, EyeOff, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';

export default function SetPasswordDialog() {
  const { showPasswordSetDialog, closePasswordSetDialog, listId } = useStore();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generatedPassword, setGeneratedPassword] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Generate a memorable password
  const generatePassword = () => {
    const adjectives = ['autumn', 'hidden', 'silent', 'cool', 'morning', 'blue', 'gentle', 'quiet'];
    const nouns = ['forest', 'river', 'mountain', 'lake', 'sunset', 'breeze', 'cloud', 'meadow'];
    const adjective = adjectives[Math.floor(Math.random() * adjectives.length)];
    const noun = nouns[Math.floor(Math.random() * nouns.length)];
    const number = Math.floor(Math.random() * 1000);
    
    const generated = `${adjective}-${noun}-${number}`;
    setPassword(generated);
    setConfirmPassword(generated);
    setGeneratedPassword(generated);
  };
  
  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      toast.success('Password copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy password');
    }
  };
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }
    
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    
    try {
        // Use the existing API client
        const { listAPI } = await import('@api/client');
        await listAPI.setPassword(listId, password);
        
        useStore.setState({ 
          hasPassword: true,
          password: password
        });
        
        // Store in session
        sessionStorage.setItem(`password_${listId}`, password);
        
        toast.success('Password protection enabled!');
        closePasswordSetDialog();
      } catch (error) {
        toast.error('Failed to set password');
      } finally {
        setIsLoading(false);
      }
  };
  
  return (
    <Dialog
      isOpen={showPasswordSetDialog}
      onClose={closePasswordSetDialog}
      title="Add Password Protection"
      description="Create a password to control who can edit this list"
    >
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
        
        {/* Generate Password Option */}
        <div style={{
          backgroundColor: '#f0f9ff',
          border: '1px solid #bae6fd',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <h4 style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            marginBottom: '8px',
            color: '#0c4a6e'
          }}>
            Need a secure password?
          </h4>
          <p style={{ fontSize: '13px', color: '#0369a1', marginBottom: '12px' }}>
            We can generate a memorable password for you
          </p>
          <button
            type="button"
            onClick={generatePassword}
            style={{
              padding: '8px 16px',
              backgroundColor: '#0284c7',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Generate Password
          </button>
        </div>

        {/* Password Input */}
        <div>
          <label htmlFor="password" style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '500', 
            marginBottom: '8px',
            color: '#374151'
          }}>
            Password
          </label>
          <div style={{ position: 'relative' }}>
            <input
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                paddingRight: password ? '80px' : '40px',
                border: `1px solid ${errors.password ? '#ef4444' : '#d1d5db'}`,
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                fontFamily: 'monospace',
                backgroundColor: errors.password ? '#fef2f2' : 'white'
              }}
              placeholder="Enter a password"
              onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
              onBlur={(e) => e.target.style.borderColor = errors.password ? '#ef4444' : '#d1d5db'}
            />
            
            <div style={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '4px'
            }}>
              {password && (
                <button
                  type="button"
                  onClick={copyPassword}
                  style={{
                    padding: '4px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center'
                  }}
                  title="Copy password"
                >
                  {copied ? 
                    <Check style={{ width: '16px', height: '16px', color: '#059669' }} /> :
                    <Copy style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                  }
                </button>
              )}
              
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  padding: '4px',
                  backgroundColor: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {showPassword ? 
                  <EyeOff style={{ width: '16px', height: '16px', color: '#6b7280' }} /> :
                  <Eye style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                }
              </button>
            </div>
          </div>
          
          {errors.password && (
            <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
              {errors.password}
            </p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" style={{ 
            display: 'block', 
            fontSize: '14px', 
            fontWeight: '500', 
            marginBottom: '8px',
            color: '#374151'
          }}>
            Confirm Password
          </label>
          <input
            type={showPassword ? 'text' : 'password'}
            id="confirmPassword"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              border: `1px solid ${errors.confirmPassword ? '#ef4444' : '#d1d5db'}`,
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              fontFamily: 'monospace',
              backgroundColor: errors.confirmPassword ? '#fef2f2' : 'white'
            }}
            placeholder="Confirm your password"
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = errors.confirmPassword ? '#ef4444' : '#d1d5db'}
          />
          {errors.confirmPassword && (
            <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
              {errors.confirmPassword}
            </p>
          )}
        </div>

        {/* Info Box */}
        <div style={{
          backgroundColor: '#f8fafc',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          padding: '16px'
        }}>
          <h4 style={{ 
            fontSize: '14px', 
            fontWeight: '500', 
            marginBottom: '8px',
            color: '#1f2937'
          }}>
            How password protection works:
          </h4>
          <ul style={{ 
            margin: 0, 
            paddingLeft: '0',
            listStyle: 'none',
            fontSize: '13px',
            color: '#4b5563'
          }}>
            <li style={{ marginBottom: '4px' }}>• Anyone can still <strong>view</strong> this list</li>
            <li style={{ marginBottom: '4px' }}>• Only people with the password can <strong>edit</strong></li>
            <li style={{ marginBottom: '4px' }}>• Share the password with trusted collaborators</li>
            <li>• You can remove the password anytime</li>
          </ul>
        </div>
        
        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', paddingTop: '8px' }}>
          <button
            type="button"
            onClick={closePasswordSetDialog}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: 'white',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: '#374151',
              cursor: 'pointer'
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            style={{
              flex: 1,
              padding: '12px',
              backgroundColor: isLoading ? '#9ca3af' : '#059669',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              color: 'white',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            {isLoading ? (
              <>
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid white',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                Setting...
              </>
            ) : (
              <>
                <Lock style={{ width: '16px', height: '16px' }} />
                Add Password
              </>
            )}
          </button>
        </div>
      </form>
    </Dialog>
  );
}